import React from "react";
import { Instagram, Facebook } from "lucide-react";

/**
 * Social-connect band — sits directly above the Footer on public pages.
 * Premium fintech aesthetic, mint-glow accents, generous touch targets.
 * Variants:
 *   default — full-bleed section (used on Landing)
 *   compact — inline strip (used inside PageShell so non-landing routes
 *             still surface the social CTAs without the full chrome)
 */
const SOCIALS = [
  {
    id: "instagram",
    label: "@gigafloatapp",
    href: "https://instagram.com/gigafloatapp",
    Icon: Instagram,
  },
  {
    id: "facebook",
    label: "@gigafloatapp",
    href: "https://facebook.com/gigafloatapp",
    Icon: Facebook,
  },
];

const SocialButton = ({ id, href, label, Icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`social-${id}-link`}
    aria-label={`Follow GigAfloat on ${id}`}
    className="group relative flex items-center gap-4 rounded-2xl bg-[#0A0A0A] border border-white/8 px-6 py-5 min-h-[64px] transition-all duration-300 hover:border-[#00FF88]/60 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-20px_rgba(0,255,136,0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00FF88]/60"
  >
    {/* hover sheen */}
    <span
      className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      style={{
        background:
          "radial-gradient(circle at 20% 0%, rgba(0,255,136,0.10), transparent 60%)",
      }}
    />
    <span className="relative w-12 h-12 rounded-xl bg-[#050505] border border-white/10 group-hover:border-[#00FF88]/60 grid place-items-center transition-colors">
      <Icon className="w-5 h-5 text-white/80 group-hover:text-[#00FF88] transition-colors" />
    </span>
    <span className="relative flex flex-col">
      <span className="font-mono-c text-[10px] uppercase tracking-[0.22em] text-white/45 group-hover:text-[#00FF88] transition-colors">
        {id}
      </span>
      <span className="font-display font-extrabold text-base text-white tracking-tight">
        {label}
      </span>
    </span>
  </a>
);

const SocialConnect = ({ variant = "default" }) => {
  if (variant === "compact") {
    return (
      <section
        data-testid="social-connect-compact"
        className="mt-20 pt-10 border-t border-white/8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-2">
              // Stay connected
            </div>
            <h3 className="font-display font-black text-xl text-white tracking-tight">
              Stay Connected With GigAfloat 💚
            </h3>
            <p className="text-white/55 text-sm mt-1.5 max-w-lg leading-relaxed">
              Follow our journey as we build financial continuity for the workers who keep the world moving.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {SOCIALS.map((s) => <SocialButton key={s.id} {...s} />)}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="social-connect"
      data-testid="social-connect-section"
      className="relative py-20 md:py-24 border-t border-white/5 bg-[#050505] overflow-hidden"
    >
      {/* ambient halo */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 65% 50% at 50% 100%, rgba(0,255,136,0.10), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
        <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-4">
          // Stay connected
        </div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
          Stay Connected With GigAfloat 💚
        </h2>
        <p className="mt-5 text-white/65 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
          Follow our journey as we build financial continuity for the workers who keep the world moving.
        </p>
        <p className="mt-3 text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">
          Follow us here for updates, pilot announcements, founder insights, product progress, and future Float opportunities.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-stretch justify-center gap-3 sm:gap-4 max-w-2xl mx-auto">
          {SOCIALS.map((s) => <SocialButton key={s.id} {...s} />)}
        </div>
      </div>
    </section>
  );
};

export default SocialConnect;
